import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';

import type {
  EyewearAttributesFragment,
  WatchAttributesFragment,
} from '@/graphql/saleor/api';
import { getDropdownValue, getNumericValue } from '@/lib/attributes';
import type { Maybe } from '@/lib/types';
import { pdpMessages } from '@/messages/product';

export const createTechnicalDataHelpers = (
  formatMessage: (message: MessageDescriptor) => string,
) => ({
  getDropDownTechnicalData: (
    attribute: any,
    message: MessageDescriptor,
    forceUnit?: string,
  ) => {
    const attributeValue = getDropdownValue(attribute);
    return attributeValue
      ? [
          {
            name: formatMessage(message),
            value: `${attributeValue} ${forceUnit ?? ''}`,
          },
        ]
      : [];
  },

  getNumericTechnicalData: (
    attribute: any,
    message: MessageDescriptor,
    forceUnit?: string,
  ) => {
    const attributeValue = getNumericValue(attribute);

    const isAttributeValuePositive = attributeValue?.value
      ? parseFloat(attributeValue?.value) > 0
      : false;

    return attributeValue && isAttributeValuePositive
      ? [
          {
            name: formatMessage(message),
            value: `${Number(attributeValue.value)} ${
              forceUnit ?? attributeValue.unit
            }`,
          },
        ]
      : [];
  },
});

export function useTechnicalDataSections(
  productType: 'watch' | 'eyewear',
  product: WatchAttributesFragment | EyewearAttributesFragment,
) {
  const { formatMessage } = useIntl();
  const { getDropDownTechnicalData, getNumericTechnicalData } =
    createTechnicalDataHelpers(formatMessage);

  if (productType === 'eyewear' && isEyewearAttributesFragment(product)) {
    return [
      {
        title: formatMessage(pdpMessages.technicalDataEyewearFrameDescription),
        attributes: [
          ...getDropDownTechnicalData(
            product.frameShape,
            pdpMessages.technicalDataEyewearFrameDescriptionShape,
          ),
          ...getDropDownTechnicalData(
            product.frameColor,
            pdpMessages.technicalDataEyewearFrameDescriptionColor,
          ),
          ...getDropDownTechnicalData(
            product.frameMaterial,
            pdpMessages.technicalDataEyewearFrameDescriptionMaterial,
          ),
          ...getDropDownTechnicalData(
            product.coreWire,
            pdpMessages.eyewearCoreWire,
          ),
          ...getDropDownTechnicalData(
            product.templeTipDetail,
            pdpMessages.eyewearTempleTipDetail,
          ),
          ...getDropDownTechnicalData(
            product.frontalPins,
            pdpMessages.eyewearFrontalPins,
          ),
        ],
      },
      {
        title: formatMessage(pdpMessages.technicalDataEyewearLens),
        attributes: [
          ...getDropDownTechnicalData(
            product.lensSurface,
            pdpMessages.technicalDataEyewearLensSurface,
          ),
          ...getDropDownTechnicalData(
            product.lensColor,
            pdpMessages.technicalDataEyewearLensColor,
          ),
          ...getDropDownTechnicalData(
            product.lensCoating,
            pdpMessages.technicalDataEyewearLensCoating,
          ),
          ...getDropDownTechnicalData(
            product.uvProtection,
            pdpMessages.technicalDataEyewearLensUvProtection,
          ),
        ],
      },
      {
        title: formatMessage(pdpMessages.technicalDataEyewearDimensions),
        attributes: [
          ...getNumericTechnicalData(
            product.frontalWidth,
            pdpMessages.technicalDataEyewearDimensionsFrontalWidth,
          ),
          ...getNumericTechnicalData(
            product.templeLength,
            pdpMessages.technicalDataEyewearDimensionsTempleLength,
          ),
          ...getNumericTechnicalData(
            product.lensWidth,
            pdpMessages.technicalDataEyewearDimensionsLensWidth,
          ),
          ...getNumericTechnicalData(
            product.bridgeWidth,
            pdpMessages.technicalDataEyewearDimensionsBridgeWidth,
          ),
        ],
      },
    ];
  }

  if (isWatchAttributesFragment(product))
    return [
      {
        title: formatMessage(pdpMessages.technicalDataMovement),
        attributes: [
          ...getDropDownTechnicalData(
            product.caliber,
            pdpMessages.technicalDataMovementCaliber,
          ),
          ...getDropDownTechnicalData(
            product.movement,
            pdpMessages.technicalDataMovementMovement,
          ),
          ...getDropDownTechnicalData(
            product.powerReserve,
            pdpMessages.technicalDataMovementPowerReserve,
          ),
          ...getDropDownTechnicalData(
            product.chronograph,
            pdpMessages.technicalDataMovementChronograph,
          ),
          ...getDropDownTechnicalData(
            product.vibration,
            pdpMessages.technicalDataMovementVibration,
          ),
          ...getDropDownTechnicalData(
            product.jewel,
            pdpMessages.technicalDataMovementJewel,
          ),
          ...getDropDownTechnicalData(
            product.calendar,
            pdpMessages.technicalDataMovementCalendar,
          ),
        ],
      },
      {
        title: formatMessage(pdpMessages.technicalDataCase),
        attributes: [
          ...getDropDownTechnicalData(
            product.caseMaterial,
            pdpMessages.technicalDataCaseMaterial,
          ),
          ...getDropDownTechnicalData(
            product.caseback,
            pdpMessages.technicalDataCaseCaseback,
          ),
          ...getDropDownTechnicalData(
            product.waterResistance,
            pdpMessages.technicalDataCaseWaterResistance,
          ),
          ...getDropDownTechnicalData(
            product.bezel,
            pdpMessages.technicalDataCaseBezel,
          ),
          ...getDropDownTechnicalData(
            product.crown,
            pdpMessages.technicalDataCaseCrown,
          ),
          ...getDropDownTechnicalData(
            product.crystal,
            pdpMessages.technicalDataCaseCrystal,
          ),
        ],
      },
      {
        title: formatMessage(pdpMessages.technicalDataDimensions),
        attributes: [
          ...getNumericTechnicalData(
            product.weight,
            pdpMessages.technicalDataDimensionsProductWeight,
          ),
          ...getNumericTechnicalData(
            product.headWeight,
            pdpMessages.technicalDataDimensionsWatchHeadWeight,
          ),
          ...getNumericTechnicalData(
            product.diameter,
            pdpMessages.technicalDataDimensionsDiameter,
            'mm',
          ),
          ...getNumericTechnicalData(
            product.thickness,
            pdpMessages.technicalDataDimensionsThickness,
            'mm',
          ),
          ...getNumericTechnicalData(
            product.height,
            pdpMessages.technicalDataDimensionsHeight,
            'mm',
          ),
          ...getNumericTechnicalData(
            product.lugWidth,
            pdpMessages.technicalDataDimensionsLugWidth,
            'mm',
          ),
        ],
      },
      {
        title: formatMessage(pdpMessages.technicalDataStrap),
        attributes: [
          ...getDropDownTechnicalData(
            product.strapMaterial,
            pdpMessages.technicalDataStrapStrapMaterial,
          ),
          ...getDropDownTechnicalData(
            product.strapColor,
            pdpMessages.technicalDataStrapStrapColor,
          ),
          ...getDropDownTechnicalData(
            product.strapType,
            pdpMessages.technicalDataStrapStrapType,
          ),
          ...getDropDownTechnicalData(
            product.lug,
            pdpMessages.technicalDataStrapStrapLug,
          ),
          ...getDropDownTechnicalData(
            product.buckleMaterial,
            pdpMessages.technicalDataStrapStrapBuckleMaterial,
          ),
          ...getDropDownTechnicalData(
            product.buckleType,
            pdpMessages.technicalDataStrapStrapBuckleType,
          ),
          ...getDropDownTechnicalData(
            product.buckleSize,
            pdpMessages.technicalDataStrapStrapBuckleSize,
          ),
        ],
      },
    ];

  return [];
}

export function isWatchAttributesFragment(
  product:
    | Maybe<WatchAttributesFragment | EyewearAttributesFragment>
    | undefined,
): product is WatchAttributesFragment {
  return (
    typeof product === 'object' &&
    product !== null &&
    'waterResistance' in product
  );
}

export function isEyewearAttributesFragment(
  product:
    | Maybe<WatchAttributesFragment | EyewearAttributesFragment>
    | undefined,
): product is EyewearAttributesFragment {
  return (
    typeof product === 'object' && product !== null && 'lensWidth' in product
  );
}
